import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useVersion } from 'react-admin';
import { Box, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { SnackbarContainer } from 'components/standalone';
import { extractErrorMessage } from 'utils';
import { SupplierContainer, SupplierFormValues } from '../containers';
import { SupplierForm } from '../components';

const SuppliersEditInner = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { notify } = SnackbarContainer.useContainer();
  const {
    selectedSupplier,
    isLoading,
    supplierLoaded,
    supplierError,
    formLoading,
    supplierRegions,
    supplierRepresentatives,
    supplierRegionsLoaded,
    supplierRepresentativesLoaded,
    regionsError,
    representativesError,
    loadSupplierById,
    updateSupplier,
    loadRegionOptions,
    loadUserRepresentativeOptions,
    loadSupplierRegionsForSupplier,
    loadSupplierRepresentativesForSupplier,
    batchUpdateSupplierRegions,
    batchUpdateSupplierRepresentatives,
    setFormLoading,
    resetLoadedFlags,
  } = SupplierContainer.useContainer();

  const version = useVersion();

  const supplierId = selectedSupplier?.id;

  useEffect(() => {
    if (!id) return;
    loadSupplierById(id);
  }, [id, version, loadSupplierById]);

  useEffect(() => {
    if (!supplierId) return;
    resetLoadedFlags();
    loadSupplierRegionsForSupplier(supplierId);
    loadSupplierRepresentativesForSupplier(supplierId);
  }, [
    supplierId,
    resetLoadedFlags,
    loadSupplierRegionsForSupplier,
    loadSupplierRepresentativesForSupplier,
  ]);

  useEffect(() => {
    if (supplierError || (supplierLoaded && !selectedSupplier)) {
      notify('Supplier not found', 'error');
      history.push('/supplier');
    }
  }, [supplierError, supplierLoaded, selectedSupplier, notify, history]);

  const initialRegionIds = useMemo(
    () => supplierRegions.map(sr => sr.regionId),
    [supplierRegions],
  );

  const initialRepresentativeIds = useMemo(
    () => supplierRepresentatives.map(sr => sr.userId),
    [supplierRepresentatives],
  );

  const regionsLoading = !!supplierId && !supplierRegionsLoaded;
  const representativesLoading =
    !!supplierId && !supplierRepresentativesLoaded;

  const handleSubmit = useCallback(
    async (values: SupplierFormValues) => {
      if (!id) return;

      setFormLoading(true);
      try {
        await updateSupplier(id, {
          name: values.name,
          externalId: values.externalId,
          description: values.description,
          email: values.email,
          phone: values.phone,
          contactInfo: values.contactInfo,
        });

        await batchUpdateSupplierRegions(
          id,
          supplierRegions,
          values.selectedRegions,
        );

        await batchUpdateSupplierRepresentatives(
          id,
          supplierRepresentatives,
          values.selectedRepresentatives,
        );

        notify('Element updated');
        history.push('/supplier');
      } catch (error: unknown) {
        console.error('Supplier update failed:', error);
        notify(
          extractErrorMessage(error, 'Error while updating the supplier'),
          'error',
        );
      } finally {
        setFormLoading(false);
      }
    },
    [
      id,
      updateSupplier,
      supplierRegions,
      supplierRepresentatives,
      batchUpdateSupplierRegions,
      batchUpdateSupplierRepresentatives,
      history,
      notify,
      setFormLoading,
    ],
  );

  const handleGoBack = useCallback(() => {
    history.push('/supplier');
  }, [history]);

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedSupplier) {
    return null;
  }

  return (
    <Box className={classes.formContainer}>
      <SupplierForm
        mode="edit"
        initialValues={selectedSupplier}
        initialRegionIds={initialRegionIds}
        initialRepresentativeIds={initialRepresentativeIds}
        onSubmit={handleSubmit}
        loading={formLoading}
        onGoBack={handleGoBack}
        loadRegionOptions={loadRegionOptions}
        loadUserRepresentativeOptions={loadUserRepresentativeOptions}
        regionsLoading={regionsLoading}
        representativesLoading={representativesLoading}
        regionsError={regionsError}
        representativesError={representativesError}
      />
    </Box>
  );
};

export const SuppliersEdit = () => (
  <SupplierContainer.Provider>
    <SuppliersEditInner />
  </SupplierContainer.Provider>
);

const useStyles = makeStyles(theme => ({
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
}));

export default SuppliersEdit;
